import "../css/profile-form.css";
import React, { useEffect, useState } from "react";
import { formatToDateString } from "../../event/components/utils/utils";
import { UserProfile } from "../types/profile";
import ReactFlagsSelect from "react-flags-select";
import { countryListData } from "../data/countryData";
import { profileAPI } from "../utils/API";
import Swal from "sweetalert2";
import { LoaderSm } from "../../common/components/Loader/Loader";

interface IProps {
  userData: UserProfile;
  onCreate?: (data: UserProfile) => Promise<UserProfile>;
  onUpdate?: (data: UserProfile, slug: string) => Promise<UserProfile>;
}

const ProfileUpdateForm: React.FC<IProps> = ({ userData, onUpdate, onCreate }) => {
  const [id, setId] = useState<string | undefined>(userData?._id);
  const [fullName, setFullName] = useState<string>(userData?.fullName);
  const [phoneNumber, setPhoneNumber] = useState<string>(userData?.mobile);
  const [email, setEmail] = useState<string>(userData?.email);
  const [birthDate, setBirthDate] = useState(
    formatToDateString(new Date(userData?.dateOfBirth || new Date()))
  );
  const [gender, setGender] = useState<string>(userData?.gender);
  const [organizationName, setOrganizationName] = useState<string>(
    userData?.organisation
  );
  const [city, setCity] = useState<string>(userData?.city);
  const [country, setCountry] = useState<string>(userData?.country);
  const [countryCode, setCountryCode] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const selectedCountryCode = countryListData.find(
      (c) => c.name === userData.country
    )?.code;
    if (selectedCountryCode) {
      setCountryCode(selectedCountryCode);
    }
  }, [userData.country]);

  const handleSelect = (code: string) => {
    setCountryCode(code);
    const selectedCountry = countryListData.find((c) => c.code === code)?.name;
    if (selectedCountry) setCountry(selectedCountry);
  };

  const updateGenderTo = (value: string) => {
    setGender(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const slug = id || userData?._id;
    const profileData: UserProfile = {
      fullName,
      email,
      city,
      country,
      dateOfBirth: birthDate,
      gender,
      mobile: phoneNumber,
      organisation: organizationName,
      userId: userData.userId,
    };

    setIsLoading(true);
    try {
      const res = id? await onUpdate!(profileData, slug ?? "") : await onCreate!(profileData);

      if(!id && res) { 
        setId(res._id)
      };

      if (res) {
        await Swal.fire({
          position: "center",
          icon: "success",
          title: `Profile ${userData._id ? "Updated" : "Created"} Successfully`,
          showConfirmButton: false,
          timer: 1500,
        });
      }
    } catch (error) {
      console.error(error);
      await Swal.fire({
        position: "center",
        icon: "error",
        title: "Something went wrong",
        showConfirmButton: false,
        timer: 1500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-card">
        <header className="profile-header">
          <h2 className="profile-title">Profile settings</h2>
          <p className="profile-subtitle">
            Keep your personal and organisation details up to date so we can
            tailor your experience.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="profile-form">
          {/* PERSONAL INFORMATION */}
          <section className="profile-section">
            <div className="profile-section__header">
              <h3 className="profile-section__title">Personal information</h3>
              <p className="profile-section__subtitle">
                Basic details to help us personalise your account.
              </p>
            </div>

            <div className="profile-form__grid">
              {/* Full name */}
              <div className="profile-form-group">
                <label htmlFor="fullName">
                  Full name <span className="profile-required">*</span>
                </label>
                <div className="profile-input">
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Your full name"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div className="profile-form-group">
                <label htmlFor="email">
                  Email address <span className="profile-required">*</span>
                </label>
                <div className="profile-input">
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                  />
                </div>
              </div>

              {/* Phone : Hidden */}
              <div className="profile-form-group d-none">
                <label htmlFor="phoneNumber">Contact number</label>
                <div className="profile-input">
                  <input
                    id="phoneNumber"
                    type="text"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="+123 456 7890"
                  />
                </div>
              </div>

              {/* DOB : Hidden */}
              <div className="profile-form-group d-none">
                <label htmlFor="birthDate">Date of birth</label>
                <div className="profile-input">
                  <input
                    id="birthDate"
                    type="date"
                    value={birthDate}
                    onChange={(e) =>
                      setBirthDate(
                        formatToDateString(new Date(e.target.value))
                      )
                    }
                  />
                </div>
              </div>

              {/* Gender */}
              <div className="profile-form-group profile-form-group--full">
                <label>Gender</label>
                <div className="profile-gender-toggle">
                  <button
                    type="button"
                    className={`profile-gender-option ${
                      gender === "male" ? "profile-gender-option--active" : ""
                    }`}
                    onClick={() => updateGenderTo("male")}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    className={`profile-gender-option ${
                      gender === "female"
                        ? "profile-gender-option--active"
                        : ""
                    }`}
                    onClick={() => updateGenderTo("female")}
                  >
                    Female
                  </button>
                  <button
                    type="button"
                    className={`profile-gender-option ${
                      gender === "others"
                        ? "profile-gender-option--active"
                        : ""
                    }`}
                    onClick={() => updateGenderTo("others")}
                  >
                    Other
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* ORGANISATION INFORMATION */}
          <section className="profile-section">
            <div className="profile-section__header">
              <h3 className="profile-section__title">
                Organisation information
              </h3>
              <p className="profile-section__subtitle">
                Tell us where you work so we can map sustainability impact.
              </p>
            </div>

            <div className="profile-form__grid">
              {/* Organisation name */}
              <div className="profile-form-group profile-form-group--full">
                <label htmlFor="organisation">
                  Organisation name <span className="profile-required">*</span>
                </label>
                <div className="profile-input">
                  <input
                    id="organisation"
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="Organisation name"
                    required
                  />
                </div>
              </div>

              {/* Country */}
              <div className="profile-form-group">
                <label>
                  Country <span className="profile-required">*</span>
                </label>
                <div className="profile-flags-select">
                  <ReactFlagsSelect
                    selected={countryCode}
                    onSelect={handleSelect}
                    searchable
                  />
                </div>
              </div>

              {/* City */}
              <div className="profile-form-group">
                <label htmlFor="city">
                  City <span className="profile-required">*</span>
                </label>
                <div className="profile-input">
                  <input
                    id="city"
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="City"
                    required
                  />
                </div>
              </div>
            </div>
          </section>

          {/* ACTIONS */}
          <div className="profile-actions">
            <button
              type="submit"
              className="profile-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <LoaderSm />
                  <span className="profile-button__label">
                    Saving changes…
                  </span>
                </>
              ) : (
                "Update profile"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileUpdateForm;
